import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  TrophyIcon,
  MoneyIcon,
  LeafIcon,
  CheckIcon,
  CrossIcon
} from '../components/Icons';
import './WhyChoose.css';

const WhyChoose = () => {
  return (
    <div className="why-choose-page">
      <Helmet>
        <title>Why Choose Pure Drop Technicals - Your Trusted Water Partner</title>
        <meta
          name="description"
          content="Discover why Pure Drop Technicals is South Africa's trusted partner for water business solutions. Experience, compliance, sustainability, and proven results."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="why-hero section small-hero">
        <div className="container">
          <div className="why-hero-content">
            <div className="why-text">
              <h1>Why Choose Pure Drop?</h1>
              <p className="lead">
                Trusted water business partners for turnkey installs, compliance, and growth support across South Africa.
              </p>
              <div className="hero-cta">
                <Link to="/request" className="btn btn-primary">Get a Quote</Link>
                <Link to="#reasons" className="btn btn-outline">See Proof</Link>
              </div>
            </div>
            <div className="why-visual" aria-hidden="true">
              <div className="why-icon">
                <TrophyIcon size={56} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Reasons */}
      <section id="reasons" className="main-reasons section">
        <div className="container">
          <div className="reasons-grid">
            {/* Proven Experience */}
            <div className="reason-card">
              <div className="reason-visual">
                <div className="reason-icon">
                  <TrophyIcon size={64} className="reason-icon-svg" />
                </div>
                <div className="reason-stats">
                  <div className="stat-number">15+</div>
                  <div className="stat-label">Years Experience</div>
                </div>
              </div>
              <div className="reason-content">
                <h2>Proven Experience</h2>
                <p>
                  Over 15 years of specialized experience in water purification and business development across South Africa.
                </p>

                <div className="reason-details">
                  <h4>Our Track Record:</h4>
                  <ul>
                    <li><CheckIcon size={16} className="check-icon" /> 500+ successful water business installations</li>
                    <li><CheckIcon size={16} className="check-icon" /> 1000+ satisfied customers nationwide</li>
                    <li><CheckIcon size={16} className="check-icon" /> 95% customer satisfaction rate</li>
                    <li><CheckIcon size={16} className="check-icon" /> Zero system failures in the past 3 years</li>
                    <li><CheckIcon size={16} className="check-icon" /> Award-winning technical excellence</li>
                    <li><CheckIcon size={16} className="check-icon" /> Industry leadership in innovation</li>
                  </ul>
                </div>

                <div className="reason-highlight">
                  <strong>Industry Leader:</strong> Recognized as South Africa's premier water business solution provider
                </div>

                <div className="card-cta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                  <Link to="/services" className="btn btn-outline">See Our Services</Link>
                  <Link to="/request" className="btn btn-primary">Start a Project</Link>
                </div>
              </div>
            </div>

            {/* Cost-Effective Solutions */}
            <div className="reason-card">
              <div className="reason-visual">
                <div className="reason-icon">
                  <MoneyIcon size={64} className="reason-icon-svg" />
                </div>
                <div className="reason-stats">
                  <div className="stat-number">ROI</div>
                  <div className="stat-label">6–12 Months</div>
                </div>
              </div>
              <div className="reason-content">
                <h2>Cost-Effective & Scalable</h2>
                <p>Smart investment solutions designed to maximize your return and grow with your business ambitions.</p>

                <div className="reason-details">
                  <h4>Financial Benefits:</h4>
                  <ul>
                    <li><CheckIcon size={16} className="check-icon" /> Flexible financing options available</li>
                    <li><CheckIcon size={16} className="check-icon" /> Competitive pricing with transparent costs</li>
                    <li><CheckIcon size={16} className="check-icon" /> Energy-efficient systems reduce operating costs</li>
                    <li><CheckIcon size={16} className="check-icon" /> Scalable designs for future expansion</li>
                    <li><CheckIcon size={16} className="check-icon" /> High-quality components minimize maintenance</li>
                    <li><CheckIcon size={16} className="check-icon" /> Proven business models ensure profitability</li>
                  </ul>
                </div>

                <div className="reason-highlight">
                  <strong>Smart Investment:</strong> Average ROI achieved within 6–12 months
                </div>

                <div className="card-cta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                  <Link to="/request" className="btn btn-primary">Get a Quote</Link>
                  <Link to="/how-it-works" className="btn btn-outline">See ROI Examples</Link>
                </div>
              </div>
            </div>

            {/* Full Compliance */}
            <div className="reason-card">
              <div className="reason-visual">
                <div className="reason-icon">
                  <CheckIcon size={64} className="reason-icon-svg" />
                </div>
                <div className="reason-stats">
                  <div className="stat-number">100%</div>
                  <div className="stat-label">Compliance</div>
                </div>
              </div>
              <div className="reason-content">
                <h2>Full Compliance & Certification</h2>
                <p>All systems meet and exceed South African water quality standards and regulatory requirements.</p>

                <div className="reason-details">
                  <h4>Certifications & Standards:</h4>
                  <ul>
                    <li><CheckIcon size={16} className="check-icon" /> SANS 241 Water Quality Standards certified</li>
                    <li><CheckIcon size={16} className="check-icon" /> ISO 9001 Quality Management System</li>
                    <li><CheckIcon size={16} className="check-icon" /> Department of Water and Sanitation approved</li>
                    <li><CheckIcon size={16} className="check-icon" /> Environmental Impact Assessment certified</li>
                    <li><CheckIcon size={16} className="check-icon" /> Professional Engineering Council registered</li>
                    <li><CheckIcon size={16} className="check-icon" /> Regular third-party audits and inspections</li>
                  </ul>
                </div>

                <div className="reason-highlight">
                  <strong>Peace of Mind:</strong> Full legal compliance and certification guaranteed
                </div>

                <div className="card-cta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                  <Link to="/services" className="btn btn-outline">Explore Systems</Link>
                  <Link to="/request" className="btn btn-primary">Get Compliance Help</Link>
                </div>
              </div>
            </div>

            {/* Sustainable Practices */}
            <div className="reason-card">
              <div className="reason-visual">
                <div className="reason-icon">
                  <LeafIcon size={64} className="reason-icon-svg" />
                </div>
                <div className="reason-stats">
                  <div className="stat-number">30%</div>
                  <div className="stat-label">Water Savings</div>
                </div>
              </div>
              <div className="reason-content">
                <h2>Sustainable & Eco-Friendly</h2>
                <p>Environmentally responsible solutions that protect our planet while building your business.</p>

                <div className="reason-details">
                  <h4>Environmental Benefits:</h4>
                  <ul>
                    <li><CheckIcon size={16} className="check-icon" /> Water recovery systems reduce waste by 30%</li>
                    <li><CheckIcon size={16} className="check-icon" /> Energy-efficient designs minimize carbon footprint</li>
                    <li><CheckIcon size={16} className="check-icon" /> Recyclable and biodegradable materials used</li>
                    <li><CheckIcon size={16} className="check-icon" /> Smart monitoring reduces resource consumption</li>
                    <li><CheckIcon size={16} className="check-icon" /> Solar-powered options available</li>
                    <li><CheckIcon size={16} className="check-icon" /> Contribution to SDG 6: Clean Water and Sanitation</li>
                  </ul>
                </div>

                <div className="reason-highlight">
                  <strong>Green Business:</strong> Contribute to environmental sustainability while earning profits
                </div>

                <div className="card-cta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                  <Link to="/request" className="btn btn-primary">Design a Green System</Link>
                  <Link to="/services" className="btn btn-outline">See Options</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="competitive-advantages section">
        <div className="container">
          <div className="section-header text-center">
            <h2>What Sets Us Apart</h2>
            <p>Compare Pure Drop Technicals with other water solution providers</p>
          </div>

          {/* Optional comparison header image */}
          <div className="visual-box" style={{ marginBottom: 24 }}>
            <h3 style={{ textAlign: 'center', marginBottom: 12 }}>Comparison Overview</h3>
            <div className="shop-layout">
            </div>
          </div>

          <div className="comparison-grid">
            <div className="comparison-feature">
              <div className="feature-header">
                <h3>Comprehensive Support</h3>
              </div>
              <div className="feature-comparison">
                <div className="pure-drop">
                  <h4>Pure Drop Technicals</h4>
                  <ul>
                    <li><CheckIcon size={16} className="check-icon" /> 24/7 technical support</li>
                    <li><CheckIcon size={16} className="check-icon" /> Business training included</li>
                    <li><CheckIcon size={16} className="check-icon" /> Marketing assistance</li>
                    <li><CheckIcon size={16} className="check-icon" /> Legal compliance handling</li>
                    <li><CheckIcon size={16} className="check-icon" /> Ongoing business consulting</li>
                  </ul>
                </div>
                <div className="others">
                  <h4>Other Providers</h4>
                  <ul>
                    <li><CrossIcon size={16} className="cross-icon" /> Limited support hours</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Equipment-only focus</li>
                    <li><CrossIcon size={16} className="cross-icon" /> No marketing support</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Customer handles compliance</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Minimal post-sale service</li>
                  </ul>
                </div>
              </div>
              <div className="card-cta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                <Link to="/request" className="btn btn-primary">Get Support</Link>
                <Link to="/contact" className="btn btn-outline">Talk to Us</Link>
              </div>
            </div>

            <div className="comparison-feature">
              <div className="feature-header">
                <h3>Technology & Innovation</h3>
              </div>
              <div className="feature-comparison">
                <div className="pure-drop">
                  <h4>Pure Drop Technicals</h4>
                  <ul>
                    <li><CheckIcon size={16} className="check-icon" /> Latest RO technology</li>
                    <li><CheckIcon size={16} className="check-icon" /> IoT monitoring systems</li>
                    <li><CheckIcon size={16} className="check-icon" /> Automated quality control</li>
                    <li><CheckIcon size={16} className="check-icon" /> Mobile app integration</li>
                    <li><CheckIcon size={16} className="check-icon" /> Predictive maintenance</li>
                  </ul>
                </div>
                <div className="others">
                  <h4>Other Providers</h4>
                  <ul>
                    <li><CrossIcon size={16} className="cross-icon" /> Outdated equipment</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Manual monitoring only</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Basic quality checks</li>
                    <li><CrossIcon size={16} className="cross-icon" /> No digital integration</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Reactive maintenance</li>
                  </ul>
                </div>
              </div>
              <div className="card-cta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                <Link to="/services" className="btn btn-outline">Explore Tech</Link>
                <Link to="/request" className="btn btn-primary">Spec My System</Link>
              </div>
            </div>

            <div className="comparison-feature">
              <div className="feature-header">
                <h3>Business Success</h3>
              </div>
              <div className="feature-comparison">
                <div className="pure-drop">
                  <h4>Pure Drop Technicals</h4>
                  <ul>
                    <li><CheckIcon size={16} className="check-icon" /> Proven business models</li>
                    <li><CheckIcon size={16} className="check-icon" /> Market analysis included</li>
                    <li><CheckIcon size={16} className="check-icon" /> Customer acquisition strategies</li>
                    <li><CheckIcon size={16} className="check-icon" /> Financial planning assistance</li>
                    <li><CheckIcon size={16} className="check-icon" /> Growth scaling support</li>
                  </ul>
                </div>
                <div className="others">
                  <h4>Other Providers</h4>
                  <ul>
                    <li><CrossIcon size={16} className="cross-icon" /> No business guidance</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Customer research required</li>
                    <li><CrossIcon size={16} className="cross-icon" /> No marketing strategy</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Basic financial advice</li>
                    <li><CrossIcon size={16} className="cross-icon" /> Limited growth support</li>
                  </ul>
                </div>
              </div>
              <div className="card-cta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                <Link to="/request" className="btn btn-primary">Plan My Rollout</Link>
                <Link to="/success" className="btn btn-outline">See Success Stories</Link>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="why-cta section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Join Our Success Stories?</h2>
            <p>Experience the Pure Drop difference — where your success is our commitment</p>
            <div className="cta-buttons">
              <Link to="/request" className="btn btn-primary">Start Your Journey</Link>
              <Link to="/contact" className="btn btn-outline">Talk to Our Experts</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChoose;
